import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import type { SearchParams, SortParams } from "../components/Manager/types";
import { useEffect, useCallback } from "react";

export type Territory = {
  id: number;
  user_id: string | null;
  agency_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  agencies?: { name: string } | null;
  stores_territories?: { store_id: number }[];
  store_count?: number;
};

/**
 * Search territories by name prefix. Returns empty array for empty search string.
 * Used by autocomplete components to avoid loading all 232 territories.
 */
export async function searchTerritories(
  searchText: string,
  limit = 10,
): Promise<Territory[]> {
  if (!searchText.trim()) {
    return [];
  }

  const { data, error } = await supabase
    .from("territories")
    .select("*")
    .ilike("name", `%${searchText}%`)
    .order("name", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch a single territory by ID. Used to display the selected territory name.
 */
export async function fetchTerritoryById(
  id: number,
): Promise<Territory | null> {
  const { data, error } = await supabase
    .from("territories")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw error;
  }
  return data;
}

export type FetchResult<T> = {
  data: T[];
  hasMore: boolean;
  totalCount?: number;
};

export async function fetchTerritories(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<Territory>> {
  // First, get total count with search filters
  let countQuery = supabase
    .from("territories")
    .select("*", { count: "exact", head: true });

  // Apply text search filter for count (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    countQuery = countQuery.ilike("name", searchText);
  }

  // Apply user_id filter for count
  if (searchParams?.filters?.user_id) {
    countQuery = countQuery.eq(
      "user_id",
      searchParams.filters.user_id as string,
    );
  }

  // Apply agency_id filter for count
  if (searchParams?.filters?.agency_id) {
    countQuery = countQuery.eq(
      "agency_id",
      searchParams.filters.agency_id as number,
    );
  }

  const { count } = await countQuery;

  // Now get the data with agency name and store count
  let query = supabase.from("territories").select(`
    *,
    agencies(name),
    stores_territories(store_id)
  `);

  // Apply sorting (default to name ascending if not specified)
  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";
  query = query.order(sortColumn, { ascending: sortAscending });

  // Apply text search filter (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    query = query.ilike("name", searchText);
  }

  // Apply user_id filter
  if (searchParams?.filters?.user_id) {
    query = query.eq("user_id", searchParams.filters.user_id as string);
  }

  // Apply agency_id filter
  if (searchParams?.filters?.agency_id) {
    query = query.eq("agency_id", searchParams.filters.agency_id as number);
  }

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;

  if (error) throw error;

  // Transform data to include store_count
  const items = (data ?? []).map((item: any) => ({
    ...item,
    store_count: item.stores_territories?.length ?? 0,
  })) as Territory[];

  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

const territoriesAtom = atom<Territory[]>([]);

export function useTerritories() {
  const [territories, setTerritories] = useAtom(territoriesAtom);

  const fetchAllTerritories = useCallback(async () => {
    try {
      const { error, data } = await supabase
        .from("territories")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setTerritories(data ?? []);
    } catch (err) {
      console.error("Error fetching territories:", err);
    }
  }, [setTerritories]);

  useEffect(() => {
    fetchAllTerritories();
  }, [fetchAllTerritories]);

  return [territories, fetchAllTerritories] as const;
}

/**
 * Update territory stores (replace all) — delete+reinsert pattern.
 */
export async function updateTerritoryStores(
  territoryId: number,
  storeIds: number[],
): Promise<void> {
  // Delete all existing links
  const { error: deleteError } = await supabase
    .from("stores_territories")
    .delete()
    .eq("territory_id", territoryId);

  if (deleteError) throw deleteError;

  // Insert new links
  if (storeIds.length > 0) {
    const inserts = storeIds.map((storeId) => ({
      store_id: storeId,
      territory_id: territoryId,
    }));

    const { error: insertError } = await supabase
      .from("stores_territories")
      .insert(inserts);

    if (insertError) throw insertError;
  }
}

/**
 * Get store IDs for a territory.
 */
export async function getTerritoryStoreIds(
  territoryId: number,
): Promise<number[]> {
  const { data, error } = await supabase
    .from("stores_territories")
    .select("store_id")
    .eq("territory_id", territoryId);

  if (error) throw error;
  return (data ?? []).map((item) => item.store_id);
}
